import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ProfileService } from '../services/profile.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private authService: AuthService,
    private profileService: ProfileService,
    private router: Router,
  ) {}

  // accept route and state to forward the attempted URL to the login page
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Observable<boolean> {
    if (!this.authService.isLoggedIn()) {
      return this.redirectToLogin(state.url);
    }

    return this.profileService.getCurrentUser(true).pipe(
      map((user) => this.canAccessRoute(route, user)),
      catchError((error) => {
        // Only end the session when the backend actively rejects it (401/403).
        // Transient or server-side errors (5xx, network) must not log a valid user
        // out; fall back to the locally valid token and allow the navigation.
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          return of(this.redirectToLogin(state.url));
        }
        return of(this.canAccessRoute(route));
      }),
    );
  }

  private canAccessRoute(route: ActivatedRouteSnapshot, user?: { role?: string | null }): boolean {
    const allowedRoles = route.data?.['roles'] as string[] | undefined;
    if (!allowedRoles?.length) {
      return true;
    }

    const role = user?.role ?? this.authService.getUserRole();
    if (allowedRoles.includes(role)) {
      return true;
    }

    this.router.navigate(['/profile']);
    return false;
  }

  private redirectToLogin(returnUrl: string): false {
    this.authService.logoutUser();
    this.router.navigate(['/login'], { queryParams: { returnUrl } });
    return false;
  }
}
